import './CopyLink.scss'

import { HDSButton } from '@here/hds-react-components'
import classNames from 'classnames'
import { WeGoInput } from 'lib/custom-elements/wego-input/wego-input-react'
import { i18n } from 'lib/i18n'
import React, { useCallback, useEffect, useState } from 'react'

export interface CopyLinkProps {
  url: string
  onCopy: (url: string) => void
  className?: string
}

export default function CopyLink({ url, onCopy, className }: CopyLinkProps): JSX.Element {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [urlValue, setUrlValue] = useState<string>(url)

  const setSelect = useCallback((): void => {
    if (inputRef.current !== null && document.activeElement !== inputRef.current) {
      setTimeout(() => {
        inputRef.current && inputRef.current.select()
      })
    }
  }, [])

  const maskValue = useCallback((): void => {
    if (url.length > 27) {
      setUrlValue(`${url.slice(0, 28)}...`)
    } else {
      setUrlValue(url)
    }
  }, [url])

  const handleMouseDown = useCallback((): void => {
    navigator.clipboard !== undefined && void navigator.clipboard.writeText(url)
    setSelect()
    onCopy(url)
    setUrlValue(url)
  }, [onCopy, setSelect, url])

  useEffect(() => {
    const keydownHandler = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyC') {
        handleMouseDown()
      }
    }
    const inputElement = inputRef.current
    inputElement?.addEventListener('keydown', keydownHandler)

    return () => inputElement?.removeEventListener('keydown', keydownHandler)
  }, [handleMouseDown])

  const handleBlur = useCallback((): void => {
    maskValue()
  }, [maskValue])

  const handleInput = useCallback((): void => {
    inputRef.current !== null && (inputRef.current.value = url)
  }, [url])

  useEffect(() => {
    maskValue()
  }, [maskValue])

  useEffect(() => {
    setSelect()
  }, [setSelect])

  return (
    <div className={classNames('CopyLink', className)}>
      <div className="CopyLink__container">
        <WeGoInput
          onBlur={handleBlur}
          onInput={handleInput}
          type="url"
          className="CopyLink__input"
          ref={inputRef}
          value={urlValue}
        />
      </div>
      <HDSButton variant="link" size="large" onMouseDown={handleMouseDown}>
        {i18n('share.share-link.copy')}
      </HDSButton>
    </div>
  )
}
